import { useRuntimeConfig } from '#imports'
import type { RouteRecordRaw } from 'vue-router'

import type { Market } from '@backmarket/http-api'

import { generateWebviewRoutes } from '~/scopes/webviews/generateWebviewRoutes'
import redirectIfWebview from '~/scopes/webviews/middleware/redirectIfWebview'

import { ABOUT_US } from '../about-us/routes.constants'

import { CMS } from './routes-names'

export default ({ market }: { market: Market }): RouteRecordRaw[] => {
  const {
    FF_ENABLE_CMS_ABOUT_US,
    FF_ENABLE_CMS_CONTENT_HUB,
    FF_CMS_ENABLE_PROMISE_PAGE_COUNTRIES,
  } = useRuntimeConfig().public

  const enableCMSAboutUs = FF_ENABLE_CMS_ABOUT_US?.includes(market.countryCode)
  const enableCMSContentHub = FF_ENABLE_CMS_CONTENT_HUB?.includes(
    market.countryCode,
  )

  const staticAboutUsRoute: RouteRecordRaw = {
    path: 'about-us',
    name: ABOUT_US,
    component: () => import('~/scopes/about-us/AboutUs.vue'),
    meta: {
      cache: 'public, max-age=300',
      owners: ['bot-squad-content-front'],
    },
  }

  const enableCMSPromisePage = FF_CMS_ENABLE_PROMISE_PAGE_COUNTRIES?.includes(
    market.countryCode,
  )

  const aboutUsRoute: RouteRecordRaw = {
    path: 'about-us',
    name: CMS.ABOUT_US,
    component: () => import('./Landing.vue'),
    meta: {
      owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
      middleware: [redirectIfWebview],
      cache: 'public, max-age=43200',
    },
  }

  const cmsRoutes: RouteRecordRaw[] = [
    ...(enableCMSAboutUs ? [aboutUsRoute] : [staticAboutUsRoute]),
    {
      name: CMS.QUALITY,
      path: 'quality',
      // when cms promise is disabled, we perform a server navigation and hit creative-pages (and we don't render Landing.vue)
      component: () => import('./Landing.vue'),
      meta: {
        disableClientSideNavigation: () => !enableCMSPromisePage,
        owners: ['bot-squad-content-front'],
        cache: 'public, max-age=43200',
      },
    },
    {
      name: CMS.ARTICLE_ROOT_HUB,
      path: 'c',
      component: () => {
        if (!enableCMSContentHub) {
          return import('../article/Hub/pages/ContentHub.vue')
        }

        if (enableCMSContentHub) return import('./Landing.vue')

        return import('../article/Hub/pages/ArticlesHub.vue')
      },
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },
    {
      name: CMS.ARTICLE_HUB,
      // note the backend always return 'hub_buying_guide'. We need to use that for category hub and fix the breadcrumb logic for the root
      path: 'c/hub/:pageCategory',
      component: () => import('../article/Hub/pages/ContentHub.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },
    {
      name: CMS.ARTICLE,
      path: 'c/:category/:pageName',
      component: () => import('~/scopes/article/Article/Article.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },

    {
      name: CMS.BACK_STORIES,
      path: 'back-stories/:pageName',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },
    {
      name: CMS.BACK_STORIES_CATEGORY,
      path: 'back-stories/:pageName/:pageCategory',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },

    {
      name: CMS.BRAND_STORE,
      path: 'b/:pageName',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },
    {
      name: CMS.BRAND_STORE_CATEGORY,
      path: 'b/:pageName/:pageCategory',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },

    {
      name: CMS.BUYBACK,
      path: 'buyback/:pageName',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },

    {
      name: CMS.BUYBACK_CATEGORY,
      path: 'buyback/:pageName/:pageCategory',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },
    {
      name: CMS.CAREERS_HOME,
      path: 'careers/:pageName',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },

    {
      name: CMS.CAREERS_CATEGORY,
      path: 'careers/:pageName/:pageCategory',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },

    {
      name: CMS.EVENT,
      path: 'e/:pageName',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },
    {
      name: CMS.GIFT_FINDER_HOME,
      path: 'gift',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
      },
    },
    {
      name: CMS.GIFT_FINDER_MAIN,
      path: 'gift/:pageName',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },
    {
      name: CMS.GIFT_FINDER_EVENT,
      path: 'gift/:pageName/:pageCategory',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },

    {
      name: CMS.REFER_FRIEND,
      path: 'refer-friend',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },

    {
      name: CMS.SELLER,
      path: 'seller/:pageName',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },

    {
      name: CMS.SERVICE,
      path: 'service/:pageName',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },

    {
      name: CMS.STATEMENT,
      path: 'accessibility-statement',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },

    {
      name: CMS.END_FAST_TECH,
      path: 'end-fast-tech',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },

    {
      name: CMS.GIFTS,
      path: 'gifts',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },

    {
      name: CMS.REPAIR_AND_CARE,
      path: 'repair-and-care',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },

    {
      name: CMS.E2E_PAGE,
      path: 'e2e/:pageName',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
      },
    },

    {
      name: CMS.LIFESTYLE,
      path: 'lf/:pageName',
      component: () => import('./Landing.vue'),
      meta: {
        owners: ['bot-squad-search-and-recommendation-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },
  ]

  const webviewCMSRoutes = generateWebviewRoutes(cmsRoutes)

  return [...cmsRoutes, ...webviewCMSRoutes]
}
